// src/pages/Swap/hooks/useTokenBalances.js
import { useState, useEffect, useContext, useCallback } from 'react';
import { NetworkContext } from '../../../contexts/NetworkContext';
import { fetchTokenBalance } from '../utils/SwapUtils';

/**
 * Hook for fetching token balances with enhanced refresh capability
 * @param {object} provider - Ethers provider
 * @param {string} userAddress - User address
 * @param {object} inputToken - Input token
 * @param {object} outputToken - Output token
 * @returns {object} - Token balances and refresh functions
 */
const useTokenBalances = (provider, userAddress, inputToken, outputToken) => {
  const [inputTokenBalance, setInputTokenBalance] = useState('');
  const [outputTokenBalance, setOutputTokenBalance] = useState('');
  const [isRefreshingInput, setIsRefreshingInput] = useState(false);
  const [isRefreshingOutput, setIsRefreshingOutput] = useState(false);
  const { currentChain } = useContext(NetworkContext);

  // Function to refresh input token balance with retry capability
  const refreshInputBalance = useCallback(async (retryCount = 0) => {
    if (!provider || !userAddress || !inputToken?.symbol) {
      return;
    }

    setIsRefreshingInput(true);
    try {
      console.log(`Refreshing input token balance for ${inputToken.symbol} on ${currentChain?.name}`);
      const bal = await fetchTokenBalance(provider, userAddress, inputToken);
      console.log("Updated input balance:", bal);
      setInputTokenBalance(bal);
      
      // For wrapped tokens or native tokens, we might need to double-check as state updates
      // can be delayed on some networks
      if (
        (currentChain?.wrappedNativeToken && 
         inputToken.address?.toLowerCase() === currentChain.wrappedNativeToken.toLowerCase()) ||
        inputToken.address?.toLowerCase() === "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee"
      ) {
        if (retryCount < 2) {
          // Try one more refresh after a short delay
          setTimeout(async () => {
            console.log(`Performing follow-up refresh for ${inputToken.symbol} (retry: ${retryCount + 1})`);
            await refreshInputBalance(retryCount + 1);
          }, 1500);
        }
      }
    } catch (error) {
      console.error("Error refreshing input token balance:", error);
    } finally {
      setIsRefreshingInput(false);
    }
  }, [provider, userAddress, inputToken, currentChain?.name, currentChain?.wrappedNativeToken]);

  // Function to refresh output token balance with retry capability
  const refreshOutputBalance = useCallback(async (retryCount = 0) => {
    if (!provider || !userAddress || !outputToken?.symbol) {
      return;
    }

    setIsRefreshingOutput(true);
    try {
      console.log(`Refreshing output token balance for ${outputToken.symbol} on ${currentChain?.name}`);
      const bal = await fetchTokenBalance(provider, userAddress, outputToken);
      console.log("Updated output balance:", bal);
      setOutputTokenBalance(bal);
      
      // For wrapped tokens or native tokens, we might need to double-check as state updates
      // can be delayed on some networks
      if (
        (currentChain?.wrappedNativeToken && 
         outputToken.address?.toLowerCase() === currentChain.wrappedNativeToken.toLowerCase()) ||
        outputToken.address?.toLowerCase() === "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee"
      ) {
        if (retryCount < 2) {
          // Try one more refresh after a short delay
          setTimeout(async () => {
            console.log(`Performing follow-up refresh for ${outputToken.symbol} (retry: ${retryCount + 1})`);
            await refreshOutputBalance(retryCount + 1);
          }, 1500);
        }
      }
    } catch (error) {
      console.error("Error refreshing output token balance:", error);
    } finally {
      setIsRefreshingOutput(false);
    }
  }, [provider, userAddress, outputToken, currentChain?.name, currentChain?.wrappedNativeToken]);

  // Refresh all balances with sequential execution to ensure accurate state
  const refreshAllBalances = useCallback(async () => {
    console.log("Refreshing all token balances");
    if (inputToken?.symbol) {
      await refreshInputBalance();
    }
    
    // Short delay between refreshes
    await new Promise(resolve => setTimeout(resolve, 300));
    
    if (outputToken?.symbol) {
      await refreshOutputBalance();
    }
    
    console.log("All balances refreshed");
  }, [refreshInputBalance, refreshOutputBalance, inputToken?.symbol, outputToken?.symbol]);

  // Update input balance when input token or wallet changes
  useEffect(() => {
    const updateInputBalance = async () => {
      // Skip if we don't have the necessary data
      if (!provider || !userAddress || !inputToken?.symbol) {
        setInputTokenBalance('');
        return;
      }

      try {
        console.log(`Fetching input token balance for ${inputToken.symbol} on ${currentChain?.name}`);
        const bal = await fetchTokenBalance(provider, userAddress, inputToken);
        console.log("Received input balance:", bal);
        setInputTokenBalance(bal);
      } catch (error) {
        console.error("Error fetching input token balance:", error);
        setInputTokenBalance('0');
      }
    };
    
    // Reset balance when changing networks to avoid showing stale data
    setInputTokenBalance('');
    
    // Add a small delay to ensure provider is ready after network change
    const timer = setTimeout(() => {
      updateInputBalance();
    }, 300);
    
    return () => clearTimeout(timer);
  }, [inputToken?.symbol, inputToken?.address, provider, userAddress, currentChain?.key]);

  // Update output balance when output token or wallet changes
  useEffect(() => {
    const updateOutputBalance = async () => {
      // Skip if we don't have the necessary data
      if (!provider || !userAddress || !outputToken?.symbol) {
        setOutputTokenBalance('');
        return;
      }

      try {
        console.log(`Fetching output token balance for ${outputToken.symbol} on ${currentChain?.name}`);
        const bal = await fetchTokenBalance(provider, userAddress, outputToken);
        console.log("Received output balance:", bal);
        setOutputTokenBalance(bal);
      } catch (error) {
        console.error("Error fetching output token balance:", error);
        setOutputTokenBalance('0');
      }
    };
    
    // Reset balance when changing networks to avoid showing stale data
    setOutputTokenBalance('');
    
    // Add a small delay to ensure provider is ready after network change
    const timer = setTimeout(() => {
      updateOutputBalance();
    }, 300);
    
    return () => clearTimeout(timer);
  }, [outputToken?.symbol, outputToken?.address, provider, userAddress, currentChain?.key]);

  return { 
    inputTokenBalance, 
    outputTokenBalance,
    refreshInputBalance,
    refreshOutputBalance,
    refreshAllBalances,
    isRefreshingInput,
    isRefreshingOutput
  };
};

export default useTokenBalances;